import React, { useState, useEffect, useRef } from 'react';

import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useTemplatesExperts(getAuthHeader) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Nouveaux états pour prévisualisation et partage
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTemplate, setShareTemplate] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  
  // Données de référence
  const [categories, setCategories] = useState({ predefined: [], custom: [], all: [] });
  const [types, setTypes] = useState({ predefined: [], custom: [], all: [] });
  const [variables, setVariables] = useState({});
  
  // État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: '',
    category: '',
    html_content: '',
    tags: []
  });
  
  const [newTag, setNewTag] = useState('');
  const [newType, setNewType] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showCustomType, setShowCustomType] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  
  const editorRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [templatesRes, categoriesRes, typesRes, variablesRes] = await Promise.all([
        axios.get(`${API}/expert-templates`, { headers: getAuthHeader() }),
        axios.get(`${API}/expert-templates/categories`, { headers: getAuthHeader() }),
        axios.get(`${API}/expert-templates/types`, { headers: getAuthHeader() }),
        axios.get(`${API}/expert-templates/variables`, { headers: getAuthHeader() })
      ]);
      
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
      setTypes(typesRes.data);
      setVariables(variablesRes.data);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      type: '',
      category: '',
      html_content: '',
      tags: []
    });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      type: template.type,
      category: template.category,
      html_content: template.html_content,
      tags: template.tags || []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.name || !formData.type || !formData.category || !formData.html_content) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        type: formData.type,
        category: formData.category,
        html_content: formData.html_content,
        tags: formData.tags
      };

      if (editingTemplate) {
        // Mise à jour
        await axios.put(
          `${API}/expert-templates/${editingTemplate.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Template mis à jour avec succès !');
      } else {
        // Création
        await axios.post(
          `${API}/expert-templates`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Template créé avec succès !');
      }

      setShowModal(false);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/expert-templates/${templateId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Template supprimé avec succès !');
      fetchInitialData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axios.put(
        `${API}/expert-templates/${template.id}`,
        { is_active: !template.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(`Template ${template.is_active ? 'désactivé' : 'activé'} !`);
      fetchInitialData();
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const _response = await axios.post(
        `${API}/expert-templates/${template.id}/duplicate`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Template "${template.name}" dupliqué avec succès !`);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  // Prévisualiser un template
  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  // Générer le HTML complet pour impression/PDF
  const generateFullHTML = (template) => {
    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${template.name} - Expert Template</title>
  <style>
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
    h1, h2, h3 { color: #059669; }
    .header { border-bottom: 2px solid #059669; padding-bottom: 20px; margin-bottom: 30px; }
    .header h1 { margin: 0; font-size: 24px; }
    .header p { margin: 5px 0 0; color: #666; }
    .meta { display: flex; gap: 20px; margin-bottom: 20px; font-size: 14px; color: #666; }
    .meta span { background: #f0fdf4; padding: 4px 12px; border-radius: 4px; }
    .content { margin-top: 20px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${template.name}</h1>
    <p>${template.description || ''}</p>
  </div>
  <div class="meta">
    <span>Type: ${template.type}</span>
    <span>Catégorie: ${template.category}</span>
    <span>Statut: ${template.is_active ? 'Actif' : 'Inactif'}</span>
  </div>
  <div class="content">
    ${template.html_content || '<p>Aucun contenu</p>'}
  </div>
  <div class="footer">
    <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} - O'SCOP Expert Templates</p>
  </div>
</body>
</html>`;
  };

  // Imprimer un template
  const handlePrintTemplate = (template) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generateFullHTML(template));
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
      toast.success('Impression lancée');
    } else {
      toast.error("Impossible d'ouvrir la fenêtre d'impression");
    }
  };

  // Télécharger en PDF (via HTML)
  const handleDownloadPDF = async (template) => {
    try {
      const htmlContent = generateFullHTML(template);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.name.replace(/[^a-z0-9]/gi, '_')}_expert_template.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Template téléchargé (utilisez Imprimer > Enregistrer en PDF)');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Ouvrir le dialogue de partage
  const handleShareTemplate = (template) => {
    setShareTemplate(template);
    setShareEmail('');
    setShareMessage(`Bonjour,\n\nVeuillez trouver ci-joint le template Expert "${template.name}".\n\nCordialement`);
    setShowShareDialog(true);
  };

  // Envoyer le partage par email
  const handleSendShare = async () => {
    if (!shareEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }

    try {
      await axios.post(
        `${API}/expert-templates/${shareTemplate.id}/share`,
        {
          email: shareEmail,
          message: shareMessage
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Template partagé avec ${shareEmail}`);
      setShowShareDialog(false);
    } catch {
      // Fallback: copy share link to clipboard
      const shareLink = `${window.location.origin}/templates/expert/${shareTemplate.id}`;
      navigator.clipboard.writeText(shareLink);
      toast.info('Lien copié dans le presse-papiers (email non disponible)');
      setShowShareDialog(false);
    }
  };

  // Copier le lien de partage
  const handleCopyShareLink = () => {
    const shareLink = `${window.location.origin}/templates/expert/${shareTemplate.id}`;
    navigator.clipboard.writeText(shareLink);
    toast.success('Lien copié dans le presse-papiers');
  };

  const handleAddTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const insertVariable = (variable) => {
    if (editorRef.current) {
      // Pour contentEditable, on insère le texte à la position du curseur
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(variable));
        range.collapse(false);
      } else {
        // Si pas de sélection, on ajoute à la fin
        editorRef.current.innerHTML += variable;
      }
      // Mettre à jour le state
      setFormData({ ...formData, html_content: editorRef.current.innerHTML });
    }
  };

  // Fonction pour générer et télécharger le document HTML
  const handleGenerateDocument = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formData.name}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
      color: #333;
    }
    h1, h2, h3 { color: #0a3d62; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    td, th { border: 1px solid #ddd; padding: 12px; }
    th { background-color: #f8f9fa; }
  </style>
</head>
<body>
  ${formData.html_content}
</body>
</html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.name.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Document HTML généré et téléchargé avec succès!');
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setFormData({ ...formData, html_content: editorRef.current.innerHTML });
    }
  };

  const insertHeading = (level) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const heading = document.createElement(`h${level}`);
      heading.textContent = selection.toString() || `Titre niveau ${level}`;
      range.deleteContents();
      range.insertNode(heading);
      setFormData({ ...formData, html_content: editorRef.current.innerHTML });
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    const matchesType = filterType === 'all' || template.type === filterType;
    return matchesSearch && matchesCategory && matchesType;
  });

  return { applyFormat, categories, editingTemplate, editorRef, fetchInitialData, filterCategory, filterType, filteredTemplates, formData, generateFullHTML, handleAddTag, handleCopyShareLink, handleCreateNew, handleDelete, handleDownloadPDF, handleDuplicate, handleEdit, handleGenerateDocument, handlePreviewTemplate, handlePrintTemplate, handleRemoveTag, handleSave, handleSendShare, handleShareTemplate, handleToggleActive, insertHeading, insertVariable, loading, newCategory, newTag, newType, previewTemplate, searchQuery, setCategories, setEditingTemplate, setFilterCategory, setFilterType, setFormData, setLoading, setNewCategory, setNewTag, setNewType, setPreviewTemplate, setSearchQuery, setShareEmail, setShareMessage, setShareTemplate, setShowCustomCategory, setShowCustomType, setShowModal, setShowPreview, setShowShareDialog, setTemplates, setTypes, setVariables, shareEmail, shareMessage, shareTemplate, showCustomCategory, showCustomType, showModal, showPreview, showShareDialog, templates, types, variables };
}
